import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@volca/ui/lib/toast";
import type {
  ApAssignee,
  ApDocumentType,
  ApPage,
  ApPurchaseDocument,
} from "@/accounts-payable/types";
import { useApi } from "@/core/api";
import { documentKey } from "./document-key";
import { AP_DOCUMENT_ENDPOINTS, assigneesKey, reviewQueueKey } from "./keys";

export const useGetApAssignees = (businessId?: string | null) => {
  const api = useApi();
  return useQuery({
    queryKey: assigneesKey(businessId),
    queryFn: () =>
      api
        .get<ApAssignee[]>(`/businesses/${businessId}/ap/assignees`)
        .then((response) => response.data),
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000,
  });
};

const withAssignee = (
  data: { pages: ApPage<ApPurchaseDocument>[] } | undefined,
  documentId: string,
  assignee: ApAssignee | null,
) =>
  data && {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((item) =>
        item.id === documentId ? { ...item, assignee } : item,
      ),
    })),
  };

export const useAssignDocument = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const endpoints = AP_DOCUMENT_ENDPOINTS[documentType];
  const pageKeys = [endpoints.listKey(businessId), reviewQueueKey(businessId)];
  return useMutation({
    mutationFn: (vars: { documentId: string; assignee: ApAssignee | null }) =>
      api
        .post<ApPurchaseDocument>(
          `/businesses/${businessId}/ap/${endpoints.path}/${vars.documentId}/assign`,
          { assignee_id: vars.assignee?.id ?? null },
        )
        .then((response) => response.data),
    onMutate: ({ documentId, assignee }) => {
      for (const queryKey of pageKeys) {
        queryClient.setQueriesData<{ pages: ApPage<ApPurchaseDocument>[] }>(
          { queryKey, predicate: (query) => query.queryKey[2] !== "detail" },
          (data) => withAssignee(data, documentId, assignee),
        );
      }
      queryClient.setQueryData<ApPurchaseDocument>(
        documentKey(documentType, businessId, documentId),
        (document) => document && { ...document, assignee },
      );
    },
    onError: () =>
      toast.error("Couldn't change who it's assigned to. Please try again."),
    onSettled: () => {
      for (const queryKey of pageKeys) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
};
